"use client"

import { useState, useEffect } from "react"
import { FaEdit, FaTrash, FaSearch, FaPlus, FaFilter } from "react-icons/fa"
import { Link } from "react-router-dom"
import Sidebar from "../components/Sidebar.js"
import AddChallengeModal from "../components/AddChallengeModal.js"
import { getChallenges, togglePublish, deleteChallenge } from "../comman/Challenges.js"

const ChallengeDetails = () => {
  const [challenges, setChallenges] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [challengeToEdit, setChallengeToEdit] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedSport, setSelectedSport] = useState("All")

  const fetchChallenges = async () => {
    try {
      setLoading(true)
      const fetchedChallenges = await getChallenges()
      setChallenges(fetchedChallenges)
      setLoading(false)
    } catch (error) {
      setError("Error fetching challenges. Please try again.")
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchChallenges()
  }, [])

  const handleEdit = (challenge) => {
    setChallengeToEdit(challenge)
    setIsModalOpen(true)
  }

  const handleDelete = async (challengeId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this challenge?")
    if (confirmDelete) {
      try {
        await deleteChallenge(challengeId)
        setChallenges((prevChallenges) => prevChallenges.filter((challenge) => challenge._id !== challengeId))
      } catch (error) {
        setError("Error deleting challenge. Please try again.")
      }
    }
  }

  const handleAddChallenge = (newChallenge) => {
    setChallenges((prevChallenges) => [...prevChallenges, { ...newChallenge, _id: Date.now() }])
    setIsModalOpen(false)
    fetchChallenges()
  }

  const handleUpdateChallenge = (updatedChallenge) => {
    setChallenges((prevChallenges) =>
      prevChallenges.map((challenge) =>
        challenge._id === updatedChallenge._id ? { ...challenge, ...updatedChallenge } : challenge,
      ),
    )
    setIsModalOpen(false)
    setChallengeToEdit(null)
  }

  const handleTogglePublish = async (challengeId) => {
    try {
      const updatedChallenge = await togglePublish(challengeId)
      const updatedChallenges = challenges.map((challenge) =>
        challenge._id === challengeId ? { ...challenge, isPublish: updatedChallenge.isPublish } : challenge,
      )
      setChallenges(updatedChallenges)
    } catch (error) {
      setError("Error toggling publish status. Please try again.")
    }
  }

  const groupedChallenges = challenges.reduce((acc, challenge) => {
    const sport = challenge.sport || "Unknown"
    if (!acc[sport]) acc[sport] = []
    acc[sport].push(challenge)
    return acc
  }, {})

  const sports = ["All", ...Object.keys(groupedChallenges)]

  const filteredChallenges =
    selectedSport === "All" ? challenges : challenges.filter((challenge) => challenge.sport === selectedSport)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64 w-full max-w-[100vw] overflow-hidden">
        <div className="max-w-full overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Challenge Management</h1>
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 overflow-hidden">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="flex items-center space-x-4 w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                    placeholder="Search challenges"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaSearch className="w-5 h-5 text-gray-500" />
                  </div>
                </div>
                <div className="relative w-full sm:w-48">
                  <select
                    className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F] appearance-none"
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                  >
                    {sports.map((sport) => (
                      <option key={sport} value={sport}>
                        {sport}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaFilter className="w-5 h-5 text-gray-500" />
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
              >
                <FaPlus size={18} className="mr-2" />
                Add Challenge
              </button>
            </div>
            <div className="overflow-x-auto -mx-4 md:-mx-6">
              <div className="inline-block min-w-full align-middle p-4 md:p-6">
                <div className="overflow-hidden border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[#12143F]">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Challenge Name
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Sport
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Level
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Category
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Coins
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Publish
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Actions
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Submissions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredChallenges
                        .filter((challenge) => {
                          const category = challenge.category || ""
                          const coins = challenge.coins != null ? challenge.coins.toString() : ""
                          return (
                            category.toLowerCase().includes(searchQuery.toLowerCase()) || coins.includes(searchQuery)
                          )
                        })
                        .map((challenge) => (
                          <tr key={challenge._id} className="hover:bg-gray-50 transition duration-150">
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">{challenge.name}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{challenge.sport}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{challenge.level}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[150px] truncate">
                              {challenge.category}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{challenge.coins}</td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={challenge.isPublish}
                                  onChange={() => handleTogglePublish(challenge._id)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                              </label>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-center space-x-3">
                                <button
                                  onClick={() => handleEdit(challenge)}
                                  className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200"
                                >
                                  <FaEdit size={18} />
                                </button>
                                <button
                                  onClick={() => handleDelete(challenge._id)}
                                  className="text-red-500 hover:text-red-700 transition-colors duration-200"
                                >
                                  <FaTrash size={18} />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <Link
                                to={`/challengeapprovalpage/${challenge._id}`}
                                className="text-blue-500 hover:text-blue-700 transition-colors duration-200"
                              >
                                View Submissions
                              </Link>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddChallengeModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setChallengeToEdit(null)
        }}
        onAddChallenge={handleAddChallenge}
        onUpdateChallenge={handleUpdateChallenge}
        challengeToEdit={challengeToEdit}
      />
    </div>
  )
}

export default ChallengeDetails

